import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { BsImage } from "react-icons/bs";
import Handler from "../../Helpers/Handler";
import { textCapitalize } from "../../Helpers/Utils";

// Segment/subSegment options come from the live site-settings segment map
// (Pages/Settings/tabs/SegmentTab.js writes it) rather than a hardcoded
// list here - modelCreate/modelEdit validate against exactly that map, so
// this stays correct if an admin ever adds a segment there. Seats is
// required (4-9) for a car brand's model, optional (1-2) for a bike brand's
// - both enforced client-side to match VehicleValidation's seatsValidator.
// 2026-09-16: brandId/brandType are now optional props. Opened from a brand
// page they are supplied and the Brand field is hidden; opened from the
// standalone Models page they are absent and the admin picks a brand here,
// which is also where brandType (and therefore the seats rule) comes from.
const ModelFormModal = ({ mode = "create", brandId, brandType, model, onClose, onSaved }) => {
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);
    const logoFileRef = useRef(null);
    const needsBrandPicker = mode === "create" && !brandId;
    const [brands, setBrands] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(needsBrandPicker);
    const [selectedBrandId, setSelectedBrandId] = useState(brandId || "");
    const [modelName, setModelName] = useState(model?.modelName || "");
    const [segment, setSegment] = useState(model?.segment || "");
    const [subSegment, setSubSegment] = useState(model?.subSegment || "");
    const [seats, setSeats] = useState(model?.seats ?? "");
    const [logoPreview, setLogoPreview] = useState(model?.logo || "");
    const [segmentMap, setSegmentMap] = useState({});
    const [loadingSegments, setLoadingSegments] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        (async () => {
            setLoadingSegments(true);
            const response = await Handler({ method: "GET", url: "/admin/business/site/segment/view" });
            setSegmentMap(response?.success ? response?.data?.data || {} : {});
            setLoadingSegments(false);
        })();
    }, []);

    useEffect(() => {
        if (!needsBrandPicker) return;
        (async () => {
            setLoadingBrands(true);
            const response = await Handler({ method: "GET", url: "/admin/vehicle/brand/active" });
            setBrands(response?.success ? response?.data?.data || [] : []);
            setLoadingBrands(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // brandType drives the seats rule (4-9 cars, 1-2 bikes) and matches what
    // VehicleValidation's seatsValidator enforces server-side. Prefer the
    // prop; fall back to the picked brand's own type.
    const effectiveBrandType = useMemo(
        () => brandType || brands.find((b) => b.brandId === selectedBrandId)?.brandType || "",
        [brandType, brands, selectedBrandId]
    );

    const segmentOptions = useMemo(() => Object.keys(segmentMap), [segmentMap]);
    const subSegmentOptions = useMemo(() => Object.keys(segmentMap?.[segment] || {}), [segmentMap, segment]);

    const onSegmentChange = (value) => {
        setSegment(value);
        setSubSegment("");
    };

    const handleLogoFile = (e) => {
        const file = e.target.files?.[0];
        if (file) setLogoPreview(URL.createObjectURL(file));
    };

    const onSubmit = async () => {
        const name = modelName.trim();
        if (!name) {
            toast.error("Model name is required");
            return;
        }
        if (!segment || !subSegment) {
            toast.error("Select a segment and sub-segment");
            return;
        }
        if (needsBrandPicker && !selectedBrandId) {
            toast.error("Select a brand");
            return;
        }
        const seatsTrimmed = String(seats).trim();
        const n = Number(seatsTrimmed);
        if (!seatsTrimmed || !(n > 3 && n < 10)) {
            toast.error("Seats must be between 4-9 for a car model");
            return;
        }
        const logoFile = logoFileRef.current?.files?.[0];
        if (mode === "create" && !logoFile) {
            toast.error("A logo is required");
            return;
        }

        setSaving(true);
        const fd = new FormData();
        fd.append("modelName", name);
        fd.append("segment", segment);
        fd.append("subSegment", subSegment);
        if (seatsTrimmed) fd.append("seats", seatsTrimmed);
        if (logoFile) fd.append("logo", logoFile);

        const response = mode === "edit"
            ? await Handler({ method: "patch", url: `/admin/vehicle/model/${model.modelId}`, data: fd, formdata: true })
            : await Handler({ method: "post", url: `/admin/vehicle/brand/${selectedBrandId}/model`, data: fd, formdata: true });

        if (response?.success) {
            toast.success(response?.data?.message || (mode === "edit" ? "Model updated" : "Model created"));
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to save the model"));
        }
        setSaving(false);
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">{mode === "edit" ? "Edit Model" : "Add Model"}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="row g-3">
                            {needsBrandPicker ? (
                                <div className="col-12">
                                    <label className="form-label">Brand</label>
                                    <select className="form-select" value={selectedBrandId} onChange={(e) => setSelectedBrandId(e.target.value)} disabled={loadingBrands}>
                                        <option value="" disabled>{loadingBrands ? "Loading..." : "Select brand"}</option>
                                        {brands.map((b) => (
                                            <option key={b.brandId} value={b.brandId}>{b.brand}{b.brandType ? ` (${b.brandType})` : ""}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : ""}
                            <div className="col-12">
                                <label className="form-label">Model name</label>
                                <input type="text" className="form-control" value={modelName} onChange={(e) => setModelName(e.target.value)} placeholder="e.g. Swift" />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Segment</label>
                                <select className="form-select" value={segment} onChange={(e) => onSegmentChange(e.target.value)} disabled={loadingSegments}>
                                    <option value="" disabled>{loadingSegments ? "Loading..." : "Select segment"}</option>
                                    {segmentOptions.map((s) => (<option key={s} value={s} className="text-capitalize">{textCapitalize(s)}</option>))}
                                </select>
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Sub-segment</label>
                                <select className="form-select" value={subSegment} onChange={(e) => setSubSegment(e.target.value)} disabled={!segment}>
                                    <option value="" disabled>{segment ? "Select sub-segment" : "Select a segment first"}</option>
                                    {subSegmentOptions.map((s) => (<option key={s} value={s} className="text-capitalize">{textCapitalize(s)}</option>))}
                                </select>
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Seats</label>
                                <input type="number" className="form-control" value={seats} onChange={(e) => setSeats(e.target.value)} placeholder={"4-9"} />
                            </div>

                            <div className="col-12">
                                <label className="form-label d-block">Logo{mode === "create" ? "" : " (optional - keeps current if left blank)"}</label>
                                <div className="settings-image-uploader">
                                    <div className="settings-image-preview">
                                        {logoPreview ? <img src={logoPreview} alt="Logo preview" /> : <BsImage />}
                                    </div>
                                    <input ref={logoFileRef} type="file" accept="image/*" className="form-control" onChange={handleLogoFile} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal" disabled={saving}>Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>
                            {saving ? "Saving..." : mode === "edit" ? "Save changes" : "Create model"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModelFormModal;
